import fetchApiWithAuth from './fetch';

export type GoodsMpLinkStatus = {
  marketplace_auth_id: number;
  marketplace: string;
  linked: boolean;
  is_archived: boolean;
  barcode: string | null;
  seller_sku: string | null;
};

export type GoodsProps = {
  article: string;
  name: string;
  category: { id: number; name: string } | null;
  brand: { name: string } | null;
  product_remnants?: { show_for_distribution: boolean };
  is_combo: boolean;
  comboComposition?: { article: string; value: number }[];
  wb_data?: { alias?: string | null; barcode?: string | null } | null;
  ozon_data?: { alias?: string | null } | null;
  ya_data?: { alias?: string | null } | null;
  sber_data?: { alias?: string | null } | null;
  ali_data?: { alias?: string | null } | null;
  deleted_at?: Date | null;
  tags?: { id?: number; name: string }[];
  /** Статусы связи с интеграциями Ozon/WB */
  mp_links?: GoodsMpLinkStatus[];
  mp_linked_count?: number;
  mp_auth_total?: number;
  /** Баркоды из связанных mp_products */
  mp_barcodes?: string[];
};

export type GoodsQueryParams = {
  article?: string;
  name?: string;
  alias?: string;
  category?: string;
  brand?: string;
  is_combo?: boolean;
  show_for_distribution?: boolean;
  /** @deprecated */
  mp_linked?: boolean;
  /** интеграции, с которыми должна быть связь */
  mp_linked_auth_ids?: number[];
  /** интеграции, с которыми связи быть не должно */
  mp_unlinked_auth_ids?: number[];
  sorting?: string;
  page?: number;
  limit?: number;
  deleted?: boolean;
  tag_ids?: number[];
};

export type GoodsPaginatedResponse = {
  total: number;
  total_page: number;
  page: number;
  limit: number;
  data: GoodsProps[];
};

export const getAll = async (
  query?: GoodsQueryParams
): Promise<GoodsProps[] | GoodsPaginatedResponse> => {
  let url = '/api/goods';

  if (query && Object.keys(query).length > 0) {
    const params = new URLSearchParams();
    if (query.article) params.append('article', query.article);
    if (query.name) params.append('name', query.name);
    if (query.alias) params.append('alias', query.alias);
    if (query.category) params.append('category', query.category);
    if (query.brand) params.append('brand', query.brand);
    if (query.is_combo !== undefined)
      params.append('is_combo', String(query.is_combo));
    if (query.show_for_distribution !== undefined)
      params.append(
        'show_for_distribution',
        String(query.show_for_distribution)
      );
    if (query.mp_linked !== undefined)
      params.append('mp_linked', String(query.mp_linked));
    if (query.mp_linked_auth_ids?.length)
      params.append('mp_linked_auth_ids', query.mp_linked_auth_ids.join(','));
    if (query.mp_unlinked_auth_ids?.length)
      params.append(
        'mp_unlinked_auth_ids',
        query.mp_unlinked_auth_ids.join(',')
      );
    if (query.deleted !== undefined)
      params.append('deleted', String(query.deleted));
    if (query.sorting) params.append('sorting', query.sorting);
    if (query.page) params.append('page', String(query.page));
    if (query.limit) params.append('limit', String(query.limit));
    if (query.tag_ids?.length)
      params.append('tag_ids', query.tag_ids.join(','));

    url += `?${params.toString()}`;
  }

  const result = await fetchApiWithAuth(url);
  const data = await result.json();

  // Если есть параметры запроса, возвращается объект с пагинацией
  // Если нет параметров, возвращается массив
  if (query && Object.keys(query).length > 0) {
    return data as GoodsPaginatedResponse;
  }
  return data as GoodsProps[];
};

export const getOne = async (article: string): Promise<GoodsProps | null> => {
  const result = await fetchApiWithAuth(
    `/api/goods/${encodeURIComponent(article)}`,
  );
  if (result.status === 404) return null;
  const data = await result.json();
  if (!result.ok) {
    throw new Error(data?.message || 'Ошибка загрузки товара');
  }
  return data as GoodsProps;
};

export type UsedInComboRow = {
  article: string;
  name: string | null;
  brand: string | null;
  value: number;
  deleted_at?: string | Date | null;
};

export const getUsedInCombos = async (
  article: string,
): Promise<UsedInComboRow[]> => {
  const result = await fetchApiWithAuth(
    `/api/goods/${encodeURIComponent(article)}/used-in-combos`,
  );
  if (!result.ok) {
    throw new Error('Ошибка загрузки комбо');
  }
  return result.json();
};

export const create = async (props: GoodsProps) => {
  const result = await fetchApiWithAuth('/api/goods', {
    method: 'POST',
    body: JSON.stringify(props),
  });

  const data = await result.json();

  if (!result.ok) {
    const error = new Error(data?.message || 'Ошибка при создании товара');
    (error as any).response = { data, status: result.status };
    throw error;
  }

  return data as GoodsProps;
};

export const edit = async (article: string, props: Partial<GoodsProps>) => {
  const result = await fetchApiWithAuth(
    `/api/goods/?article=${encodeURI(article)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(props),
    }
  );

  const data = await result.json();

  if (!result.ok) {
    const error = new Error(data?.message || 'Ошибка при обновлении товара');
    (error as any).response = { data, status: result.status };
    throw error;
  }

  return data as GoodsProps;
};

export const remove = async (article: string) => {
  const result = await fetchApiWithAuth(
    `/api/goods/?article=${encodeURI(article)}`,
    {
      method: 'DELETE',
    }
  );

  return result;
};

export const restore = async (article: string) => {
  const result = await fetchApiWithAuth(
    `/api/goods/${encodeURI(article)}/restore`,
    {
      method: 'POST',
    }
  );
  const data = (await result.json()) as GoodsProps;
  return data;
};

export const enableFbsByFilter = async (
  query: GoodsQueryParams
): Promise<{ matched: number; enabled: number; alreadyOn: number }> => {
  const result = await fetchApiWithAuth('/api/goods/enable-fbs', {
    method: 'POST',
    body: JSON.stringify({
      article: query.article,
      name: query.name,
      alias: query.alias,
      category: query.category,
      brand: query.brand,
      is_combo: query.is_combo,
      show_for_distribution: query.show_for_distribution,
      mp_linked: query.mp_linked,
      mp_linked_auth_ids: query.mp_linked_auth_ids,
      mp_unlinked_auth_ids: query.mp_unlinked_auth_ids,
      tag_ids: query.tag_ids,
    }),
  });
  const data = await result.json();
  if (!result.ok) {
    const msg = Array.isArray(data?.message)
      ? data.message.join(', ')
      : data?.message;
    throw new Error(msg || 'Не удалось включить FBS');
  }
  return data as { matched: number; enabled: number; alreadyOn: number };
};

export const getBrands = async () => {
  const result = await fetchApiWithAuth(`/api/goods/brands`, {
    method: 'GET',
  });
  const data = (await result.json()) as { name: string }[];

  return data;
};

export const getCategories = async () => {
  const result = await fetchApiWithAuth(`/api/goods/categories`, {
    method: 'GET',
  });
  const data = (await result.json()) as { id: number; name: string }[];

  return data;
};

export const uploadXlsx = async (props: { data: File }) => {
  const localVarFormData = new FormData();
  localVarFormData.append('file', props.data);
  const result = await fetchApiWithAuth(
    `/api/goods/upload_xlsx`,
    {
      method: 'POST',
      body: localVarFormData,
    },
    true
  );

  return (await result.json()) as {
    headers: string[];
    rows: (string | null)[][];
  };
};
